import { useState, useEffect } from 'react';
import SettingsPageShell from './SettingsPageShell';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { retailerSettings } from '../../utils/api';
import { toast } from 'sonner';
import { Save, Mail, MessageSquare, ShoppingCart, CheckCircle, AlertTriangle, XCircle, DollarSign } from 'lucide-react';

const ORDER_EVENTS = [
  { key: 'order_received', label: 'Order Received', icon: ShoppingCart, channels: ['email', 'sms'] },
  { key: 'order_ready', label: 'Order Ready', icon: CheckCircle, channels: ['email', 'sms'] },
  { key: 'order_delayed', label: 'Order Delayed', icon: AlertTriangle, channels: ['email', 'sms'] },
  { key: 'order_cancelled', label: 'Order Cancelled', icon: XCircle, channels: ['email', 'sms'] },
];
const PAYOUT_EVENTS = [
  { key: 'payout_sent', label: 'Payout Sent', icon: DollarSign, channels: ['email'] },
  { key: 'payout_failed', label: 'Payout Failed', icon: AlertTriangle, channels: ['email'] },
];

export default function RetailerNotifications({ user, onLogout }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    retailerSettings.getNotifications()
      .then((res) => setPrefs(res.data.notification_prefs))
      .catch(() => toast.error('Failed to load preferences'));
  }, []);

  const toggle = (event, channel, value) => {
    setPrefs((p) => ({ ...p, [event]: { ...p[event], [channel]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await retailerSettings.updateNotifications(prefs);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return <SettingsPageShell user={user} onLogout={onLogout} title="Notifications"><div className="text-gray-500">Loading…</div></SettingsPageShell>;

  const renderRow = ({ key, label, icon: Icon, channels }) => (
    <div key={key} data-testid={`notif-row-${key}`} className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-indigo-50">
          <Icon className="w-4 h-4 text-indigo-700" />
        </div>
        <span className="font-medium text-gray-800">{label}</span>
      </div>
      <div className="flex items-center gap-6">
        {channels.includes('email') && (
          <label className="flex items-center gap-2 cursor-pointer">
            <Mail className="w-4 h-4 text-gray-500" />
            <Switch
              checked={!!prefs[key]?.email}
              onCheckedChange={(v) => toggle(key, 'email', v)}
              data-testid={`notif-${key}-email`}
            />
          </label>
        )}
        {channels.includes('sms') && (
          <label className="flex items-center gap-2 cursor-pointer">
            <MessageSquare className="w-4 h-4 text-gray-500" />
            <Switch
              checked={!!prefs[key]?.sms}
              onCheckedChange={(v) => toggle(key, 'sms', v)}
              data-testid={`notif-${key}-sms`}
            />
          </label>
        )}
      </div>
    </div>
  );

  return (
    <SettingsPageShell
      user={user}
      onLogout={onLogout}
      title="Notifications"
      subtitle="Choose how DealShaq alerts you"
      actions={
        <Button onClick={save} disabled={saving} data-testid="notifications-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      }
    >
      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Order Notifications</CardTitle>
          <div className="flex items-center gap-6 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
            <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS</span>
          </div>
        </CardHeader>
        <CardContent>
          <div>{ORDER_EVENTS.map(renderRow)}</div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Payout Notifications</CardTitle>
          <p className="text-xs text-gray-500 mt-1">Email only</p>
        </CardHeader>
        <CardContent>
          <div>{PAYOUT_EVENTS.map(renderRow)}</div>
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 px-2">
        SMS notifications require a verified phone number on your account.
      </p>
    </SettingsPageShell>
  );
}
